import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  Brain,
  ChevronRight,
  Clock,
  FileText,
  HelpCircle,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  UserRound
} from "lucide-react";

export default function AIAnalysisTutorialPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI Logo"
            />
          </Link>

          <div className="flex items-center gap-6">
            <Link
              to="/ajuda/tutoriais"
              className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block"
            >
              Central de Guias
            </Link>

            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">Ajuda</Link>
          <ChevronRight size={14} />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">Guias</Link>
          <ChevronRight size={14} />
          <span className="text-indigo-600">Atendimento Clínico</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Assistente Técnico
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span>4 min de leitura</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Atendimento Clínico
          </div>
        </div>

        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            Use o Assistente Técnico para tirar dúvidas gerais, revisar conceitos,
            organizar raciocínios e obter apoio textual dentro do OdontoAssist AI.
          </p>

          <div className="bg-indigo-50 border border-indigo-100 rounded-3xl p-6 mt-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                <Brain className="text-indigo-600" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 mt-0 mb-2">
                  Importante
                </h2>
                <p className="m-0">
                  O Assistente Técnico funciona como um chat geral e não acessa dados dos pacientes.
                  As revisões vinculadas ao prontuário aparecem naturalmente em cada fluxo clínico
                  e podem ser consultadas na aba <strong>Histórico da IA</strong>.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
            Passo a Passo em Texto
          </h2>

          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <MessageCircle className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  1. Acessando o Assistente Técnico
                </h3>
              </div>

              <p>
                Acesse a tela pelo menu lateral em <strong>Assistente Técnico</strong>.
                Essa página abre um assistente em formato de chat para perguntas técnicas
                e consultas gerais dentro do sistema.
              </p>

              <p>
                Use essa área quando quiser apoio para entender conceitos, organizar uma resposta,
                revisar uma explicação ou obter sugestões gerais relacionadas à rotina odontológica
                e administrativa.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <HelpCircle className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  2. Fazendo Perguntas Técnicas
                </h3>
              </div>

              <p>
                Digite sua pergunta no campo de mensagem do chat e envie.
                Quanto mais clara for a pergunta, melhor será a resposta.
              </p>

              <p>
                Exemplos de perguntas:
              </p>

              <ul>
                <li>Como explicar ao paciente a importância da anamnese?</li>
                <li>Quais informações devo revisar antes de um procedimento?</li>
                <li>Como organizar uma orientação pós-operatória?</li>
                <li>Como melhorar o texto de uma comunicação para paciente?</li>
                <li>Quais pontos administrativos devo conferir antes de finalizar um atendimento?</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <Stethoscope className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  3. Diferença entre o Assistente e a IA Contextual
                </h3>
              </div>

              <p>
                O <strong>Assistente Técnico</strong> é destinado a perguntas gerais.
                Ela não trabalha diretamente com um paciente selecionado.
              </p>

              <p>
                Já as revisões vinculadas a pacientes são acionadas no próprio fluxo do
                <strong> Prontuário</strong>, como anamnese, evolução, odontograma estruturado,
                documentos e financeiro. Elas não analisam imagens clínicas.
              </p>

              <div className="bg-slate-50 border rounded-2xl p-5 mt-5">
                <div className="flex items-center gap-2 font-bold text-gray-900 mb-2">
                  <UserRound size={18} />
                  Regra prática
                </div>
                <p className="m-0">
                  Se a tarefa envolver um registro do paciente, use a ação contextual na respectiva aba do prontuário.
                  Se a dúvida for conceitual, textual, operacional ou geral, use o Assistente Técnico.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <FileText className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  4. Usos Recomendados
                </h3>
              </div>

              <p>
                O Assistente Técnico pode ser usado para apoiar tarefas como:
              </p>

              <ul>
                <li>Explicar termos técnicos em linguagem mais simples;</li>
                <li>Organizar orientações para pacientes;</li>
                <li>Revisar textos administrativos ou clínicos;</li>
                <li>Criar rascunhos de instruções internas;</li>
                <li>Listar pontos de atenção em fluxos operacionais;</li>
                <li>Ajudar na interpretação geral de dúvidas sobre o uso da plataforma.</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <ShieldCheck className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  5. Cuidados ao Usar a IA
                </h3>
              </div>

              <p>
                A IA é uma ferramenta de apoio. As respostas devem ser revisadas pelo profissional
                responsável antes de qualquer decisão clínica, administrativa ou comunicação ao paciente.
              </p>

              <p>
                Evite inserir informações desnecessárias ou sensíveis quando a pergunta não exigir
                dados identificáveis. Para análises de paciente, prefira usar os recursos específicos
                do prontuário.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <Sparkles className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  6. Boas Práticas para Obter Melhores Respostas
                </h3>
              </div>

              <ul>
                <li>Faça perguntas objetivas;</li>
                <li>Informe o contexto necessário;</li>
                <li>Explique o resultado esperado;</li>
                <li>Peça respostas em formato de lista quando quiser uma conferência rápida;</li>
                <li>Peça linguagem simples quando o texto for destinado ao paciente;</li>
                <li>Revise a resposta antes de usar em documentos, mensagens ou condutas.</li>
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-white border rounded-3xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mt-0 mb-4">
              Resumo
            </h2>

            <p>
              O Assistente Técnico é um ambiente de perguntas e respostas técnicas gerais.
              Ela complementa o trabalho da equipe, mas não substitui a análise profissional.
            </p>

            <p>
              Para análises diretamente relacionadas ao paciente, use o prontuário.
              Para dúvidas gerais, textos, explicações e apoio operacional, use o Assistente Técnico.
            </p>
          </div>
        </article>
      </main>
    </div>
  );
}
